import React, { useState, useEffect } from "react";
import { Modal, Button, Upload, Table, Collapse, Select, Tag, Tooltip } from "antd";
import { UploadOutlined, DownloadOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import globalRequest from "../../../utils/global/globalRequest";
import * as Apis from "../../../utils/helper/enum";
import { useDispatch } from "react-redux";
import { changeLoader } from "../../../redux/reducers/loader";
import toast from "react-hot-toast";

const BulkUploadModal = ({ isOpen, onClose, reloadPage }) => {
  const dispatch = useDispatch();
  const [fileList, setFileList] = useState([]);
  const [parsedData, setParsedData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [rawApiResult, setRawApiResult] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [selectedBranchName, setSelectedBranchName] = useState("");
  const [selectedVendorName, setSelectedVendorName] = useState("");
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [vendorData, setVendorData] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    const fetchBranches = async () => {
      try {
        setIsLoadingBranches(true);
        const response = await globalRequest(Apis.ORDERS.GET_ALL_BRANCH_FOR_BULK, "get", {}, {}, true);
        const branchList = response?.data?.data?.data || [];
        setBranches(branchList);
      } catch (error) {
        toast.error("Unable to load branches: " + (error?.message || ""));
      } finally {
        setIsLoadingBranches(false);
      }
    };
    fetchBranches();
  }, [isOpen]);

  // ─── Preview columns ────────────────────────────────────────────────────────
  const previewColumns = [
    { title: "First Name",            dataIndex: "firstName",            key: "firstName",            width: 100, render: (text, row) => (
        <div>
          <div>{text || "-"}</div>
          {row.errors?.firstName?.map((message, idx) => (
            <div key={idx} style={{ color: "#ff4d4f", fontSize: 12 }}>{message}</div>
          ))}
        </div>
      ) },
    { title: "Last Name",             dataIndex: "lastName",             key: "lastName",             width: 100, render: (text, row) => (
        <div>
          <div>{text || "-"}</div>
          {row.errors?.lastName?.map((message, idx) => (
            <div key={idx} style={{ color: "#ff4d4f", fontSize: 12 }}>{message}</div>
          ))}
        </div>
      ) },
    { title: "Email",                 dataIndex: "email",                key: "email",                width: 180, render: (text, row) => (
        <div>
          <div>{text || "-"}</div>
          {row.errors?.email?.map((message, idx) => (
            <div key={idx} style={{ color: "#ff4d4f", fontSize: 12 }}>{message}</div>
          ))}
        </div>
      ) },
    { title: "Mobile",                dataIndex: "mobileNo",             key: "mobileNo",             width: 120, render: (text, row) => (
        <div>
          <div>{text || "-"}</div>
          {row.errors?.mobileNo?.map((message, idx) => (
            <div key={idx} style={{ color: "#ff4d4f", fontSize: 12 }}>{message}</div>
          ))}
        </div>
      ) },
    { title: "Packaging ID",          dataIndex: "packaging_type_id",    key: "packaging_type_id",    width: 100 },
    { title: "Item Sensitivity",      dataIndex: "item_sensitivity_id",  key: "item_sensitivity_id",  width: 100 },
    { title: "Location Link",         dataIndex: "maps_link",            key: "maps_link",            width: 220, render: (text, row) => (
        <div>
          <div style={{ 
            maxWidth: 210, 
            overflow: "hidden", 
            textOverflow: "ellipsis", 
            whiteSpace: "nowrap",
            cursor: "pointer",
            color: "#1890ff"
          }} 
          title={text || ""}
          >
            {text ? <a href={text} target="_blank" rel="noopener noreferrer">{text}</a> : "-"}
          </div>
          {row.errors?.maps_link?.map((message, idx) => (
            <div key={idx} style={{ color: "#ff4d4f", fontSize: 12 }}>{message}</div>
          ))}
        </div>
      ) },
    { title: "External Order Number", dataIndex: "external_order_number",key: "external_order_number",width: 150, render: (text, row) => (
        <div>
          <div>{text || "-"}</div>
          {row.errors?.external_order_number?.map((message, idx) => (
            <div key={idx} style={{ color: "#ff4d4f", fontSize: 12 }}>{message}</div>
          ))}
        </div>
      ) },
    { title: "Delivery Date",         dataIndex: "delivery_date",        key: "delivery_date",        width: 130, render: (text, row) => (
        <div>
          <div>{text || "-"}</div>
          {row.errors?.delivery_date?.map((message, idx) => (
            <div key={idx} style={{ color: "#ff4d4f", fontSize: 12 }}>{message}</div>
          ))}
        </div>
      ) },
    { title: "Time Slot",             dataIndex: "delivery_time_slot_id",key: "delivery_time_slot_id",width: 80  },
  ];

  // ─── Result columns (after upload) with DEDUCTION DETAILS ────────────────────
  const resultColumns = [
    {
      title: "Status",
      dataIndex: "success",
      key: "status",
      width: 90,
      fixed: "left",
      render: (success) =>
        success ? (
          <Tag icon={<CheckCircleOutlined />} color="success">Success</Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="error">Failed</Tag>
        ),
    },
    {
      title: "Order Number",
      dataIndex: "orderNumber",
      key: "orderNumber",
      width: 140,
      render: (text) => text || "-",
    },
    {
      title: "Customer Name",
      key: "customerName",
      width: 140,
      render: (_, r) => r.customerName || "-",
    },
    { title: "Mobile",                dataIndex: "mobileNo",             key: "mobileNo",             width: 120 },
    { title: "Email",                 dataIndex: "email",                key: "email",                width: 160, render: (t) => t || "-" },
    { title: "Branch Name",           dataIndex: "branchName",           key: "branchName",           width: 150 },
    { title: "External Order No.",    dataIndex: "externalOrderNumber",  key: "externalOrderNumber",  width: 160, render: (t) => t || "-" },
    { title: "Delivery Date",         dataIndex: "deliveryDate",         key: "deliveryDate",         width: 120, render: (t) => t || "-" },
    { title: "Time Slot",             dataIndex: "timeSlot",             key: "timeSlot",             width: 160, render: (t) => t || "-" },
    { title: "Packaging",             dataIndex: "packagingType",        key: "packagingType",        width: 120, render: (t) => t || "-" },
    { title: "Sensitivity",           dataIndex: "sensitivity",          key: "sensitivity",          width: 110, render: (t) => t || "-" },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      width: 220,
      render: (t) =>
        t ? (
          <Tooltip title={t}>
            <span style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
              {t}
            </span>
          </Tooltip>
        ) : "-",
    },
    { title: "Building No.",          dataIndex: "buildingNumber",       key: "buildingNumber",       width: 110, render: (t) => t || "-" },
    {
      title: "Lat / Lng",
      key: "latLng",
      width: 160,
      render: (_, r) =>
        r.lat && r.lng ? `${r.lat}, ${r.lng}` : "-",
    },
    {
      title: "Distance KM",
      dataIndex: "distanceKM",
      key: "distanceKM",
      width: 120,
      render: (t) => t ? Number(t).toFixed(2) : "-",
    },
    {
      title: "Base Delivery Fee",
      dataIndex: "baseDeliveryFee",
      key: "baseDeliveryFee",
      width: 140,
      render: (t) => t ? `SAR ${Number(t).toFixed(2)}` : "-",
    },
    {
      title: "Actual Delivery Fee",
      dataIndex: "deliveryFee",
      key: "deliveryFee",
      width: 140,
      render: (t) => t ? `SAR ${Number(t).toFixed(2)}` : "-",
    },
    {
      title: "Vendor Deduction",
      dataIndex: "vendor_deduction",
      key: "vendor_deduction",
      width: 140,
      render: (t) => 
        t && Number(t) > 0 ? (
          <span style={{ color: "#ff4d4f", fontWeight: 600 }}>
            SAR {Number(t).toFixed(2)}
          </span>
        ) : "-",
    },
    {
      title: "Deduction Details",
      dataIndex: "deductionDetails",
      key: "deductionDetails",
      width: 250,
      render: (details) => {
        if (!details) return "-";
        return (
          <Collapse
            size="small"
            items={[{
              key: "1",
              label: `Flatcap Deduction`,
              children: (
                <div style={{ fontSize: 12 }}>
                  <div><strong>Reason:</strong> {details.reason}</div>
                  <div><strong>Type:</strong> {details.deductionType}</div>
                  <div><strong>Base Fee:</strong> SAR {Number(details.baseDeliveryFee).toFixed(2)}</div>
                  <div><strong>Flatcap Amount:</strong> SAR {Number(details.flatCapAmount).toFixed(2)}</div>
                  <div><strong>Deduction Amount:</strong> SAR {Number(details.vendor_deduction).toFixed(2)}</div>
                  <div><strong>Applied At:</strong> {new Date(details.appliedAt).toLocaleString()}</div>
                </div>
              ),
            }]}
          />
        );
      },
    },
    {
      title: "Error Details",
      dataIndex: "errorDetails",
      key: "errorDetails",
      width: 220,
      render: (errors) => {
        if (!errors || errors.length === 0) return "-";
        return (
          <Collapse
            size="small"
            items={[{
              key: "1",
              label: `${errors.length} Error(s)`,
              children: (
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {errors.map((err, idx) => <li key={idx} style={{ color: "#ff4d4f" }}>{err}</li>)}
                </ul>
              ),
            }]}
          />
        );
      },
    },
  ];

  // ─── Export successful orders to Excel ─────────────────────────────────────
  const handleExport = () => {
    if (!rawApiResult?.success?.length) {
      toast.error("No successful orders to export.");
      return;
    }

    const rows = rawApiResult.success.map((item, idx) => {
      const o = item.orderRow || {};
      const deductionDetails = item.deductionDetails || {};
      return {
        "S.No":                    idx + 1,
        "Order Number":            item.order?.data?.orderNumber || "",
        "Customer First Name":     o.firstName        || "",
        "Customer Last Name":      o.lastName         || "",
        "Customer Full Name":      `${o.firstName || ""} ${o.lastName || ""}`.trim(),
        "Mobile No":               o.mobileNo         || "",
        "Email":                   o.email            || "",
        "Branch Name":             o.branchName || o.branch_name || "",
        "Branch ID":               o.branchId   || o.branch_id   || "",
        "External Order Number":   o.external_order_number || "",
        "Delivery Date":           o.delivery_date    || "",
        "Delivery Type":           o.delivery_type    || "",
        "Time Slot":               o.time_slot_value  || "",
        "Packaging Type":          o.packaging_type_name || "",
        "Packaging Type ID":       o.packaging_type_id   || "",
        "Item Sensitivity":        o.item_sensitivity_name || o.sensitivity_name || "",
        "Item Sensitivity ID":     o.item_sensitivity_id  || "",
        "Prep Time (min)":         o.prep_time        || "",
        "Latitude":                o.lat              || "",
        "Longitude":               o.lng              || "",
        "Building Number":         o.buildingN || o.buildingNumber || "",
        "Address":                 o.address          || "",
        "Time Slot ID (Mapped)":   item.mappedIds?.time_slot_id     || "",
        "Packaging ID (Mapped)":   item.mappedIds?.packaging_type_id || "",
        "Sensitivity ID (Mapped)": item.mappedIds?.item_sensitivity_id || "",
        "Distance KM":             item.distanceKM || "",
        "Base Delivery Fee":       item.baseDeliveryFee || "",
        "Actual Delivery Fee":     item.deliveryFee || "",
        "Vendor Deduction":        item.vendor_deduction || "",
        "Deduction Reason":        deductionDetails.reason || "",
        "Deduction Type":          deductionDetails.deductionType || "",
        "Flatcap Amount":          deductionDetails.flatCapAmount || "",
      };
    });

    const ws = XLSX.utils.json_to_sheet(rows);

    // Auto column width
    const colWidths = Object.keys(rows[0] || {}).map((key) => ({
      wch: Math.max(key.length, ...rows.map((r) => String(r[key] || "").length)) + 2,
    }));
    ws["!cols"] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bulk Orders");

    const dateStr = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `bulk_orders_${dateStr}.xlsx`);
    toast.success("Export successful!");
  };

  // ─── Branch selection ───────────────────────────────────────────────────────
  const handleBranchChange = async (branchId) => {
    setSelectedBranchId(branchId);
    const br = branches.find((b) => b.id === branchId);
    setSelectedBranchName(br?.name || "");
    setSelectedVendorName("");
    setVendorData(null);
    if (!branchId) return;
    try {
      const branchResponse = await globalRequest(Apis.MANAGE_VENDOR.BRANCH_DETAIL + "/" + branchId, "get", {}, {}, true);
      const branchData = branchResponse?.data?.data;
      if (branchData?.userId) {
        const vendorResponse = await globalRequest(Apis.MANAGE_VENDOR.GET_VENDOR_DETAIL + "/" + branchData.userId, "get", {}, {}, true);
        const vd = vendorResponse?.data?.data;
        if (vd) {
          setSelectedVendorName(`${vd.firstName || ""} ${vd.lastName || ""}`.trim());
          setVendorData(vd);
        }
      }
    } catch (error) {
      toast.error("Unable to fetch branch/vendor details: " + (error?.message || ""));
    }
  };

  // ─── Reliable mobile number parser ─────────────────────────────────────────
  // Handles: number (987654321), scientific string ("9.88E+08"), plain string ("987654321")
  const parseMobileNo = (val) => {
    if (val === null || val === undefined || val === "") return "";
    // Already a safe integer stored by JS (no scientific notation risk)
    if (typeof val === "number" && Number.isInteger(val)) return String(val);
    // Float like 987654321.0 — round to remove floating-point noise
    if (typeof val === "number") return String(Math.round(val));
    const str = String(val).trim();
    // Scientific notation string: "9.88E+08", "9.88e+08", "9.88E8"
    if (/^-?\d+(\.\d+)?[eE][+-]?\d+$/.test(str)) {
      // Use BigInt-safe conversion via toFixed to avoid precision loss
      const num = Number(str);
      return Number.isInteger(num) ? String(num) : String(Math.round(num));
    }
    // Strip any non-digit characters that aren't a leading +
    return str.replace(/[^\d+]/g, "").replace(/^\+/, "");
  };

  const normalizeDate = (val) => {
    if (!val) return "";
    const s = String(val).trim();
    const dashMatch = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dashMatch) return `${dashMatch[3]}-${dashMatch[2]}-${dashMatch[1]}`;
    const slashMatch = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (slashMatch) return `${slashMatch[3]}-${slashMatch[2]}-${slashMatch[1]}`;
    return s;
  };

  // ─── File parsing ───────────────────────────────────────────────────────────
  const handleBeforeUpload = (file) => {
    setFileList([file]);
    setIsUploading(true);
    setUploadResults(null);
    setRawApiResult(null);

    const isCsv = file.name.endsWith(".csv");

    const processData = async (rawData) => {
      try {
        const processedData = rawData.map((row, index) => {
          const mobileNo = parseMobileNo(row.mobileNo ?? row.mobileno ?? row.mobile ?? "");
          const maps_link = String(
            row.maps_link      ||
            row.mapsLink       ||
            row.location_link  ||
            row["Location Link"] ||
            row["Address Link"]  ||
            row["maps link"]     ||
            row["address_link"]  ||
            ""
          ).trim();
          return { ...row, mobileNo, maps_link, key: index + 2, errors: {} };
        });

        const validatedData = [];
        for (const row of processedData) {
          const errors = {};
          const getString = (value) => String(value ?? "").trim();
          const firstName = getString(row.firstName || row.firstname || row.first_name);
          const lastName = getString(row.lastName || row.lastname || row.last_name);
          const email = getString(row.email ?? row.Email ?? "");
          const externalOrderNumber = getString(row.external_order_number || row.externalOrderNumber || row["External Order Number"] || "");
          const mapsLink = getString(
            row.maps_link || 
            row.mapsLink || 
            row.location_link || 
            row.address_link ||
            row.addressLink ||
            row["Location Link"] || 
            row["Address Link"] ||
            row["location_link"] ||
            row["address_link"] ||
            row["maps link"] ||
            ""
          );
          const deliveryDate = getString(row.delivery_date || row.deliveryDate || row["Delivery Date"] || "");

          if (!firstName) errors.firstName = ["First name is required"];
          if (!lastName) errors.lastName = ["Last name is required"];
          if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            errors.email = ["Invalid email format"];
          }
          if (!externalOrderNumber) {
            errors.external_order_number = ["External order number is required"];
          } else if (externalOrderNumber.length < 5) {
            errors.external_order_number = ["External order number must be at least 5 characters"];
          } else if (externalOrderNumber.length > 14) {
            errors.external_order_number = ["External order number must not exceed 14 characters"];
          }
          if (!row.mobileNo) {
            errors.mobileNo = ["Mobile number is required"];
          } else if (!/^\d{9}$/.test(row.mobileNo.replace(/\D/g, ""))) {
            errors.mobileNo = ["Mobile number must be 9 digits"];
          }
          if (deliveryDate && !/^(\d{2}[-\/]\d{2}[-\/]\d{4}|\d{4}-\d{2}-\d{2})$/.test(deliveryDate)) {
            errors.delivery_date = ["Invalid date format"];
          } else if (deliveryDate) {
            let parsedDate;
            if (deliveryDate.includes('-') || deliveryDate.includes('/')) {
              const parts = deliveryDate.split(/[-\/]/);
              if (parts.length === 3) {
                if (parts[2].length === 4) {
                  parsedDate = new Date(parts[2], parts[1] - 1, parts[0]);
                } else {
                  parsedDate = new Date(parts[0], parts[1] - 1, parts[2]);
                }
              }
            } else {
              parsedDate = new Date(deliveryDate);
            }
            
            if (parsedDate && !isNaN(parsedDate.getTime())) {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              parsedDate.setHours(0, 0, 0, 0);
              
              if (parsedDate < today) {
                errors.delivery_date = ["Delivery date must be today or future date"];
              }
            } else {
              errors.delivery_date = ["Invalid date format"];
            }
          }
          if (!mapsLink) {
            errors.maps_link = ["Google Maps link is required"];
          } else if (!/maps|google|goo\.gl/.test(mapsLink.toLowerCase())) {
            errors.maps_link = ["Invalid Google Maps link"];
          }

          validatedData.push({
            ...row,
            email,
            external_order_number: externalOrderNumber,
            delivery_date: deliveryDate,
            maps_link: mapsLink,
            errors,
          });
        }

        setParsedData(validatedData);
        setIsUploading(false);
      } catch (error) {
        toast.error("Error processing data: " + error.message);
        setIsUploading(false);
      }
    };

    if (isCsv) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: false,
        complete: (results) => processData(results.data),
        error: (error) => { toast.error("Error parsing CSV: " + error.message); setIsUploading(false); },
      });
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const workbook = XLSX.read(e.target.result, {
            type: "array",
            raw: true,
            cellDates: false,
          });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const json = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: true });
          processData(json);
        } catch (error) {
          toast.error("Error parsing Excel: " + error.message);
          setIsUploading(false);
        }
      };
      reader.onerror = () => { toast.error("Error reading file"); setIsUploading(false); };
      reader.readAsArrayBuffer(file);
    }
    return false;
  };

  const handleRemove = () => { setFileList([]); setParsedData([]); setUploadResults(null); setRawApiResult(null); };

  // ─── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!selectedBranchId) { toast.error("Please select a branch before uploading bulk orders"); return; }
    if (!vendorData) { toast.error("Vendor details not loaded. Please select a valid branch."); return; }
    if (!vendorData?.vendorDetail?.settings?.walletToggle) { toast.error("Prepaid wallet is not enabled for this vendor."); return; }
    if (!(vendorData?.vendorDetail?.walletBalance > 0)) { toast.error("Vendor wallet balance must be positive to create bulk orders."); return; }
    if (parsedData.length === 0) { toast.error("No data to upload"); return; }
    if (parsedData.some((row) => row.errors && Object.keys(row.errors).length > 0)) {
      return;
    }

    const ordersData = parsedData.map((row) => {
        const mobileNo = parseMobileNo(row.mobileNo ?? row.mobileno ?? row.mobile ?? "0000000000") || "0000000000";

        const rawPkg = row.packaging_type_id || row.packagingTypeId || row.packagingId || row.packaging_type_name || row.packagingName || row.packaging || "";
        const isNumericPkg = rawPkg && /^\d+$/.test(String(rawPkg).trim());
        const rawSens = row.item_sensitivity_id || row.sensitivityId || row.sensitivity_id || row.item_sensitivity_name || row.sensitivityName || row.sensitivity || "";
        const isNumericSens = rawSens && /^\d+$/.test(String(rawSens).trim());
        const rawSlot = row.delivery_time_slot_id || row.timeSlotId || row.time_slot_id || row.time_slot_value || "";
        const isNumericSlot = rawSlot && /^\d+$/.test(String(rawSlot).trim());

        const firstName = String(row.firstName || row.firstname || row.first_name || "Bulk").trim();
        const lastName  = String(row.lastName  || row.lastname  || row.last_name  || "Customer").trim();
        const deliveryDate = normalizeDate(row.delivery_date || row.deliveryDate || row.delivery_da);
        const buildingN = String(row.buildingN || row.buildingNumber || row.building_number || "").trim();

        return {
          branch_id:             selectedBranchId,
          branch_name:           selectedBranchName || null,
          firstName, lastName, mobileNo,
          email:                 String(row.email || "").trim(),
          delivery_type:         String(row.delivery_type || row.deliveryType || "schedule").trim(),
          delivery_date:         deliveryDate,
          delivery_time_slot_id: isNumericSlot ? parseInt(rawSlot) : null,
          time_slot_value:       !isNumericSlot ? (String(rawSlot).trim() || null) : null,
          maps_link:             String(
            row.maps_link || 
            row.mapsLink || 
            row.location_link || 
            row.address_link ||
            row.addressLink ||
            row["Location Link"] || 
            row["Address Link"] ||
            row["location_link"] ||
            row["address_link"] ||
            row["maps link"] ||
            ""
          ).trim(),
          packaging_type_id:     isNumericPkg  ? parseInt(rawPkg)  : null,
          packaging_type_name:   !isNumericPkg ? (String(rawPkg).trim()  || null) : (row.packaging_type_name  || null),
          item_sensitivity_id:   isNumericSens ? parseInt(rawSens) : null,
          item_sensitivity_name: !isNumericSens ? (String(rawSens).trim() || null) : (row.item_sensitivity_name || null),
          prep_time:             String(row.prep_time || row.prepTime || "0").trim(),
          custom_product_price:  parseFloat(row.custom_product_price || row.price || 0) || 0,
          reference_link:        String(row.reference_link || "").trim(),
          external_order_number: String(
  row.external_order_number || row.externalOrderNumber || ""
).trim(),
          customer_details: { firstName, lastName, mobileNo, email: String(row.email || "").trim(), branch_id: selectedBranchId, branch_name: selectedBranchName },
          delivery_details: { delivery_type: String(row.delivery_type || "schedule").trim(), delivery_date: deliveryDate, delivery_time_slot_id: isNumericSlot ? parseInt(rawSlot) : null },
        };
      });

      const response = await globalRequest(Apis.ORDERS.BULK_UPLOAD, "post", { orders: ordersData }, {}, true);

      if (response?.status === 200 || response?.data?.status === "SUCCESS") {
        const result = response.data.data || {};
        setRawApiResult(result);

        // Map success rows with DEDUCTION DETAILS
        const successRows = (result.success || []).map((item, idx) => {
          const o = item.orderRow || {};
          return {
            key:                 `success-${idx}`,
            rowNumber:           idx + 1,
            success:             true,
            orderNumber:         item.order?.data?.orderNumber || "",
            customerName:        `${o.firstName || ""} ${o.lastName || ""}`.trim(),
            mobileNo:            o.mobileNo            || "",
            email:               o.email               || "",
            branchName:          o.branchName || o.branch_name || "",
            externalOrderNumber: o.external_order_number || "",
            deliveryDate:        o.delivery_date        || "",
            timeSlot:            o.time_slot_value      || "",
            packagingType:       o.packaging_type_name  || "",
            sensitivity:         o.item_sensitivity_name || o.sensitivity_name || "",
            address:             o.address              || "",
            buildingNumber:      o.buildingN || o.buildingNumber || "",
            lat:                 o.lat                  || "",
            lng:                 o.lng                  || "",
            distanceKM:          item.distanceKM || 0,
            baseDeliveryFee:     item.baseDeliveryFee || 0,
            deliveryFee:         item.deliveryFee || 0,
            vendor_deduction:    item.vendor_deduction || 0,
            deductionDetails:    item.deductionDetails || null,
            errorDetails:        [],
          };
        });

        const errorRows = (result.errors || []).map((item, idx) => ({
          key:                 `error-${idx}`,
          rowNumber:           successRows.length + idx + 1,
          success:             false,
          customerName:        `${item.row?.firstName || ""} ${item.row?.lastName || ""}`.trim(),
          mobileNo:            item.row?.mobileNo       || "",
          email:               item.row?.email          || "",
          branchName:          item.row?.branch_name || item.row?.branchName || "",
          externalOrderNumber: item.row?.external_order_number || "",
          deliveryDate:        item.row?.delivery_date  || "",
          timeSlot:            item.row?.time_slot_value || "",
          packagingType:       item.row?.packaging_type_name || "",
          sensitivity:         item.row?.item_sensitivity_name || "",
          address:             item.row?.address        || "",
          buildingNumber:      item.row?.buildingN || item.row?.buildingNumber || "",
          lat:                 item.row?.lat            || "",
          lng:                 item.row?.lng            || "",
          distanceKM:          0,
          baseDeliveryFee:     0,
          deliveryFee:         0,
          vendor_deduction:    0,
          deductionDetails:    null,
          errorDetails:        [item.error || "Unknown error"],
        }));

        setUploadResults([...successRows, ...errorRows]);

        const successCount = result.success?.length ?? 0;
        const failureCount = result.errors?.length ?? 0;

        if (failureCount === 0) {
          toast.success(`✓ All ${successCount} orders created successfully!`);
          if (reloadPage) reloadPage();
        } else {
          toast.error(`✗ ${failureCount} order(s) failed. See details below.`);
        }
      } else {
        toast.error(response?.data?.message || "Bulk upload failed");
      }
    } 
  

  // ─── Close & reset ──────────────────────────────────────────────────────────
  const handleClose = () => {
    setFileList([]);
    setParsedData([]);
    setUploadResults(null);
    setRawApiResult(null);
    setSelectedBranchId(null);
    setSelectedBranchName("");
    setSelectedVendorName("");
    setVendorData(null);
    onClose();
  };

  const successCount = uploadResults ? uploadResults.filter((r) => r.success).length : 0;
  const failureCount = uploadResults ? uploadResults.filter((r) => !r.success).length  : 0;
  const hasParseErrors = parsedData.some((row) => row.errors && Object.keys(row.errors).length > 0);

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <Modal
      title="Upload Bulk Orders"
      open={isOpen}
      onCancel={handleClose}
      width={1400}
      destroyOnClose
      footer={
        uploadResults ? (
          [
            <Button
              key="export"
              type="primary"
              icon={<DownloadOutlined />}
              disabled={successCount === 0}
              onClick={handleExport}
              style={{ backgroundColor: "#37846B", borderColor: "#37846B" }}
            >
              Export Successful Orders ({successCount})
            </Button>,
            <Button key="close" onClick={handleClose}>
              Close
            </Button>,
          ]
        ) : (
          [
            <Button key="cancel" onClick={handleClose}>
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              loading={isUploading}
              disabled={parsedData.length === 0}
              onClick={handleSubmit}
            >
              Submit
            </Button>,
          ]
        )
      }
    >
      {!uploadResults ? (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
            <div style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "space-around" }}>
              <Select
                placeholder="Select Branch"
                value={selectedBranchId}
                onChange={handleBranchChange}
                loading={isLoadingBranches}
                style={{ minWidth: 250 }}
                options={branches.map((b) => ({ value: b.id, label: b.name }))}
              />
              <Upload
                accept=".csv,.xlsx"
                fileList={fileList}
                beforeUpload={handleBeforeUpload}
                onRemove={handleRemove}
                maxCount={1}
              >
                <Button icon={<UploadOutlined />} style={{ width: 250 }}>Select Data File</Button>
              </Upload>
            </div>
            <div><strong>Selected Branch:</strong> {selectedBranchName || "-"}</div>
            <div><strong>Vendor Name:</strong>    {selectedVendorName || "-"}</div>
          </div>

          {hasParseErrors && (
            <div style={{ color: "#ff4d4f", marginBottom: 12 }}>
              Fix highlighted row errors before submitting.
            </div>
          )}

          {parsedData.length > 0 && (
            <>
              <h3 style={{ marginTop: 20, marginBottom: 10 }}>Preview Data ({parsedData.length} rows)</h3>
              <Table
                dataSource={parsedData}
                columns={previewColumns}
                size="small"
                pagination={{ pageSize: 5 }}
                scroll={{ x: 2000, y: 300 }}
              />
            </>
          )}
        </>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              gap: 16,
              marginBottom: 16,
              padding: "12px 16px",
              borderRadius: 8,
              background: failureCount === 0 ? "#f6ffed" : "#fff2f0",
              border: `1px solid ${failureCount === 0 ? "#b7eb8f" : "#ffccc7"}`,
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: "#888", marginBottom: 4 }}>Total Orders</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{uploadResults.length}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: "#888", marginBottom: 4 }}>Successful</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#52c41a" }}>{successCount}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: "#888", marginBottom: 4 }}>Failed</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: failureCount > 0 ? "#ff4d4f" : "#888" }}>
                {failureCount}
              </div>
            </div>
            {selectedBranchName && (
              <div style={{ flex: 2 }}>
                <div style={{ fontSize: 13, color: "#888", marginBottom: 4 }}>Branch</div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{selectedBranchName}</div>
                {selectedVendorName && (
                  <div style={{ fontSize: 13, color: "#555" }}>Vendor: {selectedVendorName}</div>
                )}
              </div>
            )}
          </div>

          <Table
            dataSource={uploadResults}
            columns={resultColumns}
            size="small"
            pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `Total ${total} rows` }}
            scroll={{ x: 2200 }}
            rowKey="key"
            rowClassName={(r) => (r.success ? "" : "bulk-upload-error-row")}
          />

          <style>{`
            .bulk-upload-error-row td { background: #fff2f0 !important; }
          `}</style>
        </>
      )}
    </Modal>
  );
};

export default BulkUploadModal;